import type {
  PipelineBoard,
  PipelineBoardSort,
  PipelineBoardSortDirection,
  PipelineCard,
  PipelineDragMove,
} from '@/pages/pipelines/common/types';

export const applyPipelineCardMove = (
  board: PipelineBoard,
  params: {
    move: PipelineDragMove;
    movedAt: string;
  },
): PipelineBoard => {
  const sourceColumn = board.columns.find((column) => column.stage === params.move.sourceStage);
  const targetColumn = board.columns.find((column) => column.stage === params.move.targetStage);
  if (!sourceColumn || !targetColumn) return board;

  const card = sourceColumn.cards.find((candidate) => candidate.id === params.move.cardId);
  if (!card) return board;

  const movedCard: PipelineCard = {
    ...card,
    stage: params.move.targetStage,
    active: !targetColumn.terminal,
    lastStageChangedAt: params.movedAt,
  };

  const columns = board.columns.map((column) => {
    if (column.stage === sourceColumn.stage) {
      const cards = column.cards.filter((candidate) => candidate.id !== params.move.cardId);
      return { ...column, cards, count: cards.length };
    }

    if (column.stage === targetColumn.stage) {
      const cards = insertCardAt(column.cards, movedCard, params.move.targetIndex);
      return { ...column, cards, count: cards.length };
    }

    return column;
  });

  const activeDelta = Number(movedCard.active) - Number(card.active);

  return {
    ...board,
    columns,
    totals: {
      ...board.totals,
      activeCards: board.totals.activeCards + activeDelta,
      nonActiveCards: board.totals.nonActiveCards - activeDelta,
    },
  };
};

export const getDefaultPipelineStage = (columns: PipelineBoard['columns']) =>
  columns.find((column) => column.cards.length > 0 && !column.terminal)?.stage ??
  columns.find((column) => !column.terminal)?.stage ??
  columns[0]?.stage;

export const sortPipelineBoardCards = (
  board: PipelineBoard,
  sort?: PipelineBoardSort,
): PipelineBoard => {
  if (!sort) return board;

  return {
    ...board,
    columns: board.columns.map((column) => ({
      ...column,
      cards: sortCards(column.cards, sort),
    })),
  };
};

const insertCardAt = (cards: PipelineCard[], card: PipelineCard, index: number) => {
  const nextCards = [...cards];
  const targetIndex = Math.max(0, Math.min(index, nextCards.length));

  nextCards.splice(targetIndex, 0, card);

  return nextCards;
};

const sortCards = (cards: PipelineCard[], sort: PipelineBoardSort) =>
  cards
    .map((card, index) => ({ card, index }))
    .sort((left, right) => {
      const comparison = sort.compareCards(left.card, right.card, sort.direction);
      return comparison || left.index - right.index;
    })
    .map(({ card }) => card);

export const compareNullableDateStrings = (
  left: string | null | undefined,
  right: string | null | undefined,
  direction: PipelineBoardSortDirection,
) => {
  const leftChangedAt = getTimestamp(left);
  const rightChangedAt = getTimestamp(right);

  if (leftChangedAt !== null && rightChangedAt !== null && leftChangedAt !== rightChangedAt) {
    return direction === 'asc' ? leftChangedAt - rightChangedAt : rightChangedAt - leftChangedAt;
  }
  if (leftChangedAt !== null && rightChangedAt === null) return -1;
  if (leftChangedAt === null && rightChangedAt !== null) return 1;

  return 0;
};

const getTimestamp = (value?: string | null) => {
  if (!value) return null;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
};

const DROP_SETTLE_DURATION_MS = 220;
const DROP_SETTLE_EASING = 'cubic-bezier(0.2, 0, 0, 1)';

// dnd-kit can't settle the dropped card on its own, because on drop it moves to a
// different column and the source column collapses (no stable node to animate back
// to). So run a one-shot FLIP: with the card already placed in its new slot,
// animate it from where it was released into that slot. Runs only on drop, so it
// never touches — and never slows — the drag itself.
export const settleDroppedCard = (cardId: string, releasedFrom: { left: number; top: number }) => {
  const node = document.querySelector<HTMLElement>(`[data-card-id="${cardId}"]`);
  if (!node || typeof node.animate !== 'function') return;

  const finalRect = node.getBoundingClientRect();
  const dx = releasedFrom.left - finalRect.left;
  const dy = releasedFrom.top - finalRect.top;
  if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;

  node.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }], {
    duration: DROP_SETTLE_DURATION_MS,
    easing: DROP_SETTLE_EASING,
  });
};
